/**
 * Procedural Click Sound Generator
 * 
 * Creates a subtle, satisfying click sound using oscillators.
 * No audio files required - fully synthesized in browser.
 */

import { PROCEDURAL_PARAMS } from '../audioConfig.js';

/**
 * Create and play a click sound
 * @param {AudioContext} ctx - Web Audio context
 * @param {GainNode} destination - Destination gain node
 */
export function createClickSound(ctx, destination) {
  if (!ctx || !destination) return;

  const params = PROCEDURAL_PARAMS.click;
  const now = ctx.currentTime;

  // Create oscillator for the click tone
  const oscillator = ctx.createOscillator();
  oscillator.type = params.type;
  oscillator.frequency.setValueAtTime(params.frequency, now);
  
  // Pitch drop for more natural click
  oscillator.frequency.exponentialRampToValueAtTime(
    params.frequency * 0.5,
    now + params.duration
  );

  // Create gain envelope
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.3, now + params.attack);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + params.duration);

  // Connect and play
  oscillator.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + params.duration + 0.01);

  // Cleanup
  oscillator.onended = () => {
    oscillator.disconnect();
    gainNode.disconnect();
  };
}

/**
 * Create a button press variant (slightly lower pitch)
 */
export function createButtonPressSound(ctx, destination) {
  if (!ctx || !destination) return;

  const now = ctx.currentTime;

  const oscillator = ctx.createOscillator();
  oscillator.type = 'sine';
  oscillator.frequency.setValueAtTime(1000, now);
  oscillator.frequency.exponentialRampToValueAtTime(600, now + 0.06);

  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(0.25, now + 0.003);
  gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.06);

  oscillator.connect(gainNode);
  gainNode.connect(destination);

  oscillator.start(now);
  oscillator.stop(now + 0.07);

  oscillator.onended = () => {
    oscillator.disconnect();
    gainNode.disconnect();
  };
}
